import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

/** Display / API order: highest severity first */
const PRIOS = ["Critical", "High", "Medium", "Low"];

/** Engineering-only: include Enhancement alongside Bug (production support & others keep 3 types). */
export function getInternalDashboardTypeConfig(includeEnhancement) {
  if (includeEnhancement) {
    return {
      matrixTypes: ["Bug", "Enhancement", "Query", "Service Request"],
      allowedTypeNorms: ["bug", "enhancement", "query", "servicerequest"],
    };
  }
  return {
    matrixTypes: ["Bug", "Query", "Service Request"],
    allowedTypeNorms: ["bug", "query", "servicerequest"],
  };
}

const STATUS_GROUPS = [
  {
    id: "inProgress",
    label: "In progress",
    aliases: ["InProgress", "in_progress", "inprogress", "In - Progress", "In - progress"],
  },
  {
    id: "notStarted",
    label: "Not started",
    aliases: ["NotStarted", "notStarted", "not_started", "Not Started", "not started"],
  },
  {
    id: "unassigned",
    label: "Unassigned",
    aliases: ["Unassigned", "unassigned"],
  },
  {
    id: "overdue",
    label: "Overdue",
    aliases: ["Overdue", "overdue"],
  },
];

function normTypeKey(name) {
  return String(name || "")
    .replace(/[\s_-]/g, "")
    .toLowerCase();
}

function isAllowedTaskType(typeName, allowedTypeNorms) {
  const n = normTypeKey(typeName);
  return allowedTypeNorms.some((a) => n === a || n.includes(a) || a.includes(n));
}

function toCanonicalTypeLabel(typeName) {
  const n = normTypeKey(typeName);
  if (n.includes("servicerequest")) return "Service Request";
  if (n.includes("enhancement")) return "Enhancement";
  if (n.includes("query")) return "Query";
  if (n.includes("bug")) return "Bug";
  return null;
}

function toCanonicalPriority(priorityName) {
  const p = String(priorityName || "").trim().toLowerCase();
  if (p === "critical") return "Critical";
  if (p === "high") return "High";
  if (p === "medium") return "Medium";
  if (p === "low") return "Low";
  return null;
}

function createEmptyPriorityTypeCounts(matrixTypes) {
  return PRIOS.reduce((acc, prio) => {
    acc[prio] = matrixTypes.reduce((row, t) => {
      row[t] = 0;
      return row;
    }, {});
    return acc;
  }, {});
}

function createEmptyStatusTypePriorityCounts(matrixTypes) {
  return STATUS_GROUPS.reduce((statusAcc, status) => {
    statusAcc[status.id] = matrixTypes.reduce((typeAcc, type) => {
      typeAcc[type] = PRIOS.reduce((prioAcc, prio) => {
        prioAcc[prio] = 0;
        return prioAcc;
      }, {});
      return typeAcc;
    }, {});
    return statusAcc;
  }, {});
}

function createEmptyStatusTotals() {
  return STATUS_GROUPS.reduce((acc, status) => {
    acc[status.id] = 0;
    return acc;
  }, {});
}

function getStatusCountFromBucket(counts, aliases) {
  if (!counts || typeof counts !== "object") return 0;
  for (const key of aliases) {
    if (Object.prototype.hasOwnProperty.call(counts, key)) {
      const n = Number(counts[key]);
      return Number.isFinite(n) ? n : 0;
    }
  }
  const lowerToVal = Object.fromEntries(
    Object.entries(counts).map(([k, v]) => [String(k).toLowerCase(), v])
  );
  for (const key of aliases) {
    const v = lowerToVal[String(key).toLowerCase()];
    if (v !== undefined) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    }
  }
  return 0;
}

/** True if this object looks like { Critical?, High?, Medium?, Low? } → type → counts. */
function looksLikePriorityBlock(block) {
  if (!block || typeof block !== "object" || Array.isArray(block)) return false;
  const keys = new Set(Object.keys(block).map((k) => String(k).toLowerCase()));
  return (
    keys.has("critical") ||
    keys.has("high") ||
    keys.has("medium") ||
    keys.has("low")
  );
}

/**
 * Legacy: `{ InternalTaskTracker: { AppsDetect: { High: { Bug: { InProgress: n, ... } } } } } }`
 * Updated: `{ AppsDetect: { Critical|High|Medium|Low: { Bug: { InProgress: n, ... } } }, ... }` (app map at root of `data`)
 *
 * When both a nested `InternalTaskTracker` map and sibling root apps exist, merge so nothing is dropped.
 * When root has several apps plus a real application literally named `InternalTaskTracker`, include it
 * (it is skipped while building `rootApps` because that key is reserved for the legacy wrapper).
 */
function resolveInternalDashboardRoot(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const wrapped = data.InternalTaskTracker;

  let nestedMap = null;
  if (wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)) {
    if (Object.values(wrapped).some((v) => looksLikePriorityBlock(v))) {
      nestedMap = { ...wrapped };
    }
  }

  const rootApps = {};
  for (const [k, v] of Object.entries(data)) {
    if (k === "InternalTaskTracker") continue;
    if (looksLikePriorityBlock(v)) {
      rootApps[k] = v;
    }
  }

  if (nestedMap && Object.keys(rootApps).length > 0) {
    return { ...rootApps, ...nestedMap };
  }
  if (nestedMap) return nestedMap;

  if (Object.keys(rootApps).length > 0) {
    const merged = { ...rootApps };
    if (
      wrapped &&
      typeof wrapped === "object" &&
      !Array.isArray(wrapped) &&
      looksLikePriorityBlock(wrapped)
    ) {
      merged.InternalTaskTracker = wrapped;
    }
    return merged;
  }

  if (Object.values(data).some((v) => looksLikePriorityBlock(v))) {
    return data;
  }
  return null;
}

/**
 * Build per-project totals and a nested Status -> Type -> Priority structure.
 * @param {object} data - API payload (InternalTaskTracker map or flat app map)
 * @param {{ includeEnhancement?: boolean }} [options] - Engineering team: include Enhancement type; production support / others omit it
 */
export function parseInternalTaskTrackerPayload(data, options = {}) {
  const { matrixTypes, allowedTypeNorms } = getInternalDashboardTypeConfig(!!options.includeEnhancement);
  const root = resolveInternalDashboardRoot(data);
  if (!root || typeof root !== "object") return [];

  const projects = [];
  for (const [appName, priorityBlock] of Object.entries(root)) {
    if (!looksLikePriorityBlock(priorityBlock)) continue;

    const priorityTypeCounts = createEmptyPriorityTypeCounts(matrixTypes);
    const priorityTotals = PRIOS.reduce((acc, p) => {
      acc[p] = 0;
      return acc;
    }, {});
    const statusTypePriorityCounts = createEmptyStatusTypePriorityCounts(matrixTypes);
    const statusTotals = createEmptyStatusTotals();

    for (const [priority, typeBlock] of Object.entries(priorityBlock)) {
      const canonicalPriority = toCanonicalPriority(priority);
      if (!canonicalPriority) continue;
      if (!typeBlock || typeof typeBlock !== "object") continue;

      for (const [typeName, counts] of Object.entries(typeBlock)) {
        const canonicalType = toCanonicalTypeLabel(typeName);
        if (!canonicalType || !isAllowedTaskType(typeName, allowedTypeNorms)) continue;
        if (!counts || typeof counts !== "object") continue;

        let typePriorityTotal = 0;
        for (const status of STATUS_GROUPS) {
          const count = getStatusCountFromBucket(counts, status.aliases);
          statusTypePriorityCounts[status.id][canonicalType][canonicalPriority] += count;
          statusTotals[status.id] += count;
          typePriorityTotal += count;
        }

        priorityTypeCounts[canonicalPriority][canonicalType] += typePriorityTotal;
        priorityTotals[canonicalPriority] += typePriorityTotal;
      }
    }

    projects.push({
      name: appName,
      priorityTypeCounts,
      priorityTotals,
      statusTypePriorityCounts,
      statusTotals,
      unassigned: statusTotals.unassigned,
      inProgress: statusTotals.inProgress,
      notStarted: statusTotals.notStarted,
      overdue: statusTotals.overdue,
    });
  }

  return projects.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

/**
 * Keep only dashboard projects that match an application from fetchLookupMasterData.
 * The internal dashboard API may return legacy or extra app keys; lookup is the source of truth.
 */
export function filterProjectsByLookupApplications(projects, applications) {
  if (!Array.isArray(projects) || projects.length === 0) return [];
  if (!Array.isArray(applications) || applications.length === 0) return [];

  return projects.filter(
    (project) => resolveApplicationIdsByName(project.name, applications).length > 0
  );
}

export function resolveApplicationIdsByName(rawName, applications) {
  if (!rawName || !Array.isArray(applications) || applications.length === 0) return [];
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const raw = norm(rawName);
  const exact = applications.find((a) => norm(a.aa_app_rl_application_name) === raw);
  if (exact) return [Number(exact.aa_app_rl_application_id)].filter((n) => !isNaN(n));
  const partial = applications.find(
    (a) =>
      norm(a.aa_app_rl_application_name).includes(raw) ||
      raw.includes(norm(a.aa_app_rl_application_name))
  );
  if (partial) return [Number(partial.aa_app_rl_application_id)].filter((n) => !isNaN(n));
  return [];
}

const STATUS_LAYOUT = [
  { id: "inProgress", label: "In progress", tone: "primary" },
  { id: "notStarted", label: "Not started", tone: "secondary" },
  { id: "unassigned", label: "Unassigned", tone: "muted" },
  { id: "overdue", label: "Overdue", tone: "danger" },
];

const DEFAULT_TOOLTIP_SIZE = { w: 320, h: 300 };

function clampTooltipToViewport(clientX, clientY, width, height) {
  if (typeof window === "undefined") return { left: clientX + 12, top: clientY + 12 };
  const pad = 10;
  const gap = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(width, vw - pad * 2);
  const h = Math.min(height, vh - pad * 2);

  let left = clientX + gap;
  let top = clientY + gap;

  if (left + w > vw - pad) left = clientX - w - gap;
  if (left < pad) left = pad;
  if (top + h > vh - pad) top = clientY - h - gap;
  if (top < pad) top = pad;

  if (left + w > vw - pad) left = vw - w - pad;
  if (top + h > vh - pad) top = vh - h - pad;

  return { left, top };
}

function buildPresetBase(applicationIds, searchPrefill = "", taskTypesForPreset) {
  const term = typeof searchPrefill === "string" ? searchPrefill.trim() : "";
  const types = Array.isArray(taskTypesForPreset) ? taskTypesForPreset : ["Bug", "Query", "Service Request"];
  return {
    applicationIds,
    selectedPriorities: [...PRIOS],
    selectedTaskTypes: [...types],
    searchMode: "advanced",
    ownershipFilter: "All",
    searchPanelExpanded: true,
    internalOverdueClientFilter: false,
    internalUnassignedClientFilter: false,
    searchTerm: term,
    releaseDateFilter: "",
  };
}

/**
 * Internal (role 4 / 5) dashboard: one card per project with drill-downs to All Tasks.
 */
export default function InternalUserDashboard({
  projects,
  applications,
  searchPrefill = "",
  includeEnhancement = false,
  viewType = "my_tasks",
}) {
  const navigate = useNavigate();
  const { matrixTypes } = useMemo(() => getInternalDashboardTypeConfig(includeEnhancement), [includeEnhancement]);
  const visibleStatusLayout = useMemo(
    () =>
      viewType === "my_team"
        ? STATUS_LAYOUT
        : STATUS_LAYOUT.filter((status) => status.id !== "unassigned"),
    [viewType]
  );
  const typeListPlain = useMemo(
    () => matrixTypes.join(", ").replace(/,([^,]*)$/, ", and$1"),
    [matrixTypes]
  );
  const shouldPresetAssignedToMe = viewType === "my_tasks";
  const [tooltipBody, setTooltipBody] = useState(null);
  const [tooltipPoint, setTooltipPoint] = useState(null);
  const [tooltipSize, setTooltipSize] = useState(DEFAULT_TOOLTIP_SIZE);
  const tipRef = useRef(null);
  const moveRafRef = useRef(0);

  const hidePriorityTooltip = useCallback(() => {
    setTooltipBody(null);
    setTooltipPoint(null);
    setTooltipSize(DEFAULT_TOOLTIP_SIZE);
  }, []);

  const showPriorityTooltip = useCallback((event, payload) => {
    setTooltipBody(payload);
    setTooltipPoint({ clientX: event.clientX, clientY: event.clientY });
  }, []);

  useEffect(() => {
    if (!tooltipBody) return undefined;

    const onMove = (e) => {
      cancelAnimationFrame(moveRafRef.current);
      moveRafRef.current = requestAnimationFrame(() => {
        setTooltipPoint({ clientX: e.clientX, clientY: e.clientY });
      });
    };

    const onScrollClose = () => hidePriorityTooltip();

    const onResize = () => {
      const el = tipRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width > 8 && r.height > 8) {
        setTooltipSize({ w: r.width, h: r.height });
      }
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("scroll", onScrollClose, true);
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      cancelAnimationFrame(moveRafRef.current);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("scroll", onScrollClose, true);
      window.removeEventListener("resize", onResize);
    };
  }, [tooltipBody, hidePriorityTooltip]);

  useLayoutEffect(() => {
    if (!tooltipBody || !tipRef.current) return undefined;
    const el = tipRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 8 && r.height > 8) {
        setTooltipSize({ w: r.width, h: r.height });
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tooltipBody]);

  const tooltipViewportPos = useMemo(() => {
    if (!tooltipBody || !tooltipPoint) return null;
    return clampTooltipToViewport(
      tooltipPoint.clientX,
      tooltipPoint.clientY,
      tooltipSize.w,
      tooltipSize.h
    );
  }, [tooltipBody, tooltipPoint, tooltipSize]);

  const goAllTasks = (preset) => {
    navigate("/all-tasks", {
      state: {
        from: "internal-dashboard",
        preset,
      },
    });
  };

  /** Dashboard-only metric: All Tasks has no "Unassigned" status — open project with default statuses. */
  const onTileUnassigned = (projectName) => {
    const applicationIds = resolveApplicationIdsByName(projectName, applications);
    goAllTasks({
      ...buildPresetBase(applicationIds, searchPrefill, matrixTypes),
      selectedStatuses: ["In Progress", "Not started"],
      internalUnassignedClientFilter: true,
      // Engineering name prefill would hide unassigned results; list everyone unassigned.
      searchTerm: "",
    });
  };

  const onTileInProgress = (projectName) => {
    const applicationIds = resolveApplicationIdsByName(projectName, applications);
    goAllTasks({
      ...buildPresetBase(applicationIds, searchPrefill, matrixTypes),
      selectedStatuses: ["In Progress"],
      ...(shouldPresetAssignedToMe ? { ownershipFilter: "Assigned" } : {}),
    });
  };

  const onTileOverdue = (projectName) => {
    const applicationIds = resolveApplicationIdsByName(projectName, applications);
    goAllTasks({
      ...buildPresetBase(applicationIds, searchPrefill, matrixTypes),
      selectedStatuses: ["In Progress", "Not started", "In - Testing"],
      internalOverdueClientFilter: true,
      ...(shouldPresetAssignedToMe ? { ownershipFilter: "Assigned" } : {}),
    });
  };

  const onTileNotStarted = (projectName) => {
    const applicationIds = resolveApplicationIdsByName(projectName, applications);
    goAllTasks({
      ...buildPresetBase(applicationIds, searchPrefill, matrixTypes),
      selectedStatuses: ["Not started"],
      ...(shouldPresetAssignedToMe ? { ownershipFilter: "Assigned" } : {}),
    });
  };

  const onViewProjectAll = (projectName) => {
    const applicationIds = resolveApplicationIdsByName(projectName, applications);
    goAllTasks({
      ...buildPresetBase(applicationIds, searchPrefill, matrixTypes),
      selectedStatuses: ["In Progress", "Not started"],
    });
  };

  if (!projects.length) {
    return (
      <div className="internal-dash internal-dash--empty">
        <p>No internal tracker projects returned for your account yet.</p>
      </div>
    );
  }

  return (
    <div className="internal-dash">
      <details className="internal-dash-details">
        <summary className="internal-dash-details-summary">
          <i className="fas fa-info-circle internal-dash-details-summary-icon" aria-hidden="true" />
          <span>How to use this board</span>
        </summary>
        <div className="internal-dash-details-body">
          <p>
            {viewType === "my_team" ? (
              <>
                Each card shows only the four workflow totals: <strong>In progress</strong>,{" "}
                <strong>Not started</strong>, <strong>Unassigned</strong>, and <strong>Overdue</strong>.
              </>
            ) : (
              <>
                Each card shows only the three workflow totals: <strong>In progress</strong>,{" "}
                <strong>Not started</strong>, and <strong>Overdue</strong>.
              </>
            )}
          </p>
          <p>
            <strong>Point at a tile</strong> to see task types ({typeListPlain}) with{" "}
            <strong>Critical / High / Medium / Low</strong> for that workflow bucket. Click
            a tile to open <strong>All Tasks</strong> with the matching workflow filter.{" "}
            {viewType === "my_team"
              ? "Unassigned stays dashboard-only and is not sent as a task status filter."
              : ""}
          </p>
        </div>
      </details>
      <div className="internal-dash-grid">
        {projects.map((p) => (
          <article key={p.name} className="internal-dash-card">
            <header className="internal-dash-card-head">
              <div className="internal-dash-card-title-wrap">
                <h2 className="internal-dash-card-title">{p.name}</h2>
                <span className="internal-dash-card-caption">
                  Four workflow tiles · point for type × priority (C/H/M/L)
                </span>
              </div>
              <button
                type="button"
                className="internal-dash-card-cta"
                onClick={() => onViewProjectAll(p.name)}
              >
                View all tasks
              </button>
            </header>
            <div className="internal-dash-workflow-lane" aria-label="Workflow counts">
              {visibleStatusLayout.map((status) => {
                const statusTotal = Number(p.statusTotals?.[status.id] || 0);
                const onStatusClick =
                  status.id === "inProgress"
                    ? () => onTileInProgress(p.name)
                    : status.id === "notStarted"
                      ? () => onTileNotStarted(p.name)
                      : status.id === "unassigned"
                        ? () => onTileUnassigned(p.name)
                        : () => onTileOverdue(p.name);

                return (
                  <button
                    key={`${p.name}-${status.id}`}
                    type="button"
                    className={`internal-dash-workflow-tile internal-dash-workflow-tile--${status.tone}`}
                    onClick={onStatusClick}
                    onPointerEnter={(e) =>
                      showPriorityTooltip(e, {
                        projectName: p.name,
                        focusStatusId: status.id,
                        focusStatusLabel: status.label,
                        statusTotals: p.statusTotals,
                        statusTypePriorityCounts: p.statusTypePriorityCounts,
                      })
                    }
                    onPointerLeave={hidePriorityTooltip}
                    aria-label={`${status.label}: ${statusTotal}. Point for ${matrixTypes.join(", ")} and Critical, High, Medium, Low breakdown.`}
                  >
                    <span className="internal-dash-workflow-tile-label">{status.label}</span>
                    <span className="internal-dash-workflow-tile-value">{statusTotal}</span>
                    <span className="internal-dash-workflow-tile-hint" aria-hidden="true">
                      Point for details
                    </span>
                  </button>
                );
              })}
            </div>
          </article>
        ))}
      </div>
      {tooltipBody &&
        tooltipPoint &&
        tooltipViewportPos &&
        createPortal(
          <div
            ref={tipRef}
            className="internal-dash-floating-tooltip internal-dash-floating-tooltip--workflow internal-dash-floating-tooltip--cursor"
            style={{ left: tooltipViewportPos.left, top: tooltipViewportPos.top }}
            role="tooltip"
          >
            <div className="internal-dash-floating-tooltip-caret" aria-hidden="true" />
            <div className="internal-dash-floating-tooltip-glow" aria-hidden="true" />
            <div className="internal-dash-tooltip-inner">
              <div className="internal-dash-floating-tooltip-head">
                <span className="internal-dash-floating-tooltip-type">{tooltipBody.projectName}</span>
                <span className="internal-dash-floating-tooltip-meta">
                  Breakdown for <strong>{tooltipBody.focusStatusLabel}</strong> — read top to bottom.
                </span>
              </div>

              <div className="internal-dash-tooltip-anim-section internal-dash-tooltip-anim-section--1">
                <p className="internal-dash-tooltip-step">
                  <span className="internal-dash-tooltip-step-num" aria-hidden="true">
                    1
                  </span>
                  All {visibleStatusLayout.length} workflow totals (same numbers as the tiles on the card)
                </p>
                <div className="internal-dash-tooltip-workflow" aria-label="All workflow totals">
                  {visibleStatusLayout.map((s, chipIdx) => {
                    const n = Number(tooltipBody.statusTotals?.[s.id] || 0);
                    const isFocus = tooltipBody.focusStatusId === s.id;
                    return (
                      <div
                        key={`wf-${s.id}`}
                        className={`internal-dash-tooltip-workflow-chip${isFocus ? " is-focus" : ""}`}
                        style={{ animationDelay: `${0.12 + chipIdx * 0.055}s` }}
                      >
                        <span className="internal-dash-tooltip-workflow-chip-label">{s.label}</span>
                        <span className="internal-dash-tooltip-workflow-chip-num">{n}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="internal-dash-tooltip-bridge" aria-hidden="true">
                <span className="internal-dash-tooltip-bridge-line" />
                <span className="internal-dash-tooltip-bridge-chevron">↓</span>
                <span className="internal-dash-tooltip-bridge-line" />
              </div>

              <div className="internal-dash-tooltip-anim-section internal-dash-tooltip-anim-section--2">
                <p className="internal-dash-tooltip-step">
                  <span className="internal-dash-tooltip-step-num" aria-hidden="true">
                    2
                  </span>
                  For <strong>{tooltipBody.focusStatusLabel}</strong>: each row is a task type; columns are
                  Critical, High, Medium, Low priority
                </p>
                <div className="internal-dash-tooltip-map-block internal-dash-tooltip-map-block--solo">
                  <div className="internal-dash-tooltip-map-block-head">
                    <span className="internal-dash-tooltip-map-block-title">{tooltipBody.focusStatusLabel}</span>
                    <span className="internal-dash-tooltip-map-block-total">
                      {Number(tooltipBody.statusTotals?.[tooltipBody.focusStatusId] || 0)}
                    </span>
                  </div>
                  <div
                    className="internal-dash-tooltip-matrix"
                    role="grid"
                    aria-label={`${tooltipBody.focusStatusLabel}: type by priority`}
                  >
                    <div className="internal-dash-tooltip-matrix-row internal-dash-tooltip-matrix-row--head">
                      <span className="internal-dash-tooltip-matrix-type">Type</span>
                      {PRIOS.map((prio) => (
                        <span key={prio} title={`${prio} priority`}>
                          {prio.charAt(0)}
                        </span>
                      ))}
                    </div>
                    {matrixTypes.map((taskType, rowIdx) => {
                      const byP =
                        tooltipBody.statusTypePriorityCounts?.[tooltipBody.focusStatusId]?.[taskType] || {};
                      return (
                        <div
                          key={taskType}
                          className="internal-dash-tooltip-matrix-row internal-dash-tooltip-matrix-row--data"
                          role="row"
                          style={{ animationDelay: `${0.26 + rowIdx * 0.075}s` }}
                        >
                          <span className="internal-dash-tooltip-matrix-type" title={taskType}>
                            {taskType}
                          </span>
                          {PRIOS.map((prio) => (
                            <span key={prio}>{Number(byP[prio] || 0)}</span>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
